// apps/share-web/src/ui/invite-view.ts
// The /invite/<token> screen: show who invited you and to which team BEFORE any
// login friction, then accept. Signed out → the login gate mounts on this same
// route, so the token never has to survive a redirect.
//
// Reuses the `.login-gate` DS card (library.css) — same centered surface,
// heading, paragraph and CTA button styling as the OTP gate. No new CSS, no
// hardcoded colors: every visual value here comes from that existing class.
import { t } from './i18n.js';
import type { InvitePreview } from '../teams-api.js';

export interface InviteViewDeps {
  token: string;
  preview: (token: string) => Promise<InvitePreview>;
  accept: (token: string) => Promise<{ ok: boolean; teamId?: string; error?: string }>;
  me: () => Promise<{ email: string } | null>;
  navigate: (path: string) => void;
  mountLogin: (host: HTMLElement, opts: { teamName: string | null; onAuthed: () => void }) => void;
  /** Signs the current (wrong-account) session out. Used by the email_mismatch
   *  "switch account" action so `doAccept` can re-drive on this SAME /invite/<token>
   *  route: signed out → the login gate mounts here, never bouncing to /login (which
   *  would redirect an already-authed visitor straight back to roleHome, stranding
   *  them signed in as the wrong account with the invite token gone from the URL).
   *  Resolves `true` on success, `false` on failure — a failed logout must surface
   *  as feedback, not silently re-drive doAccept with the same wrong-account session
   *  still active (that would just redisplay the identical mismatch screen). */
  signOut: () => Promise<boolean>;
}

// Render a centered message on the login-gate card. `onRetry`, when given, adds
// a retry button so a recoverable state (transient reachability failure, an
// unclassified accept error) is not a dead end — reusing errorScreen.retryBtn
// copy. Terminal states (expired / already-used / invalid / deactivated) pass no
// onRetry, so no button appears. The button disables on first click: onRetry
// always re-renders the host (message()/renderInvite/mountInviteView all do
// `host.innerHTML = ''`), so there is no stuck-disabled state to recover from.
function message(host: HTMLElement, text: string, onRetry?: () => void): void {
  host.innerHTML = '';
  const wrap = document.createElement('div');
  wrap.className = 'login-gate';
  const p = document.createElement('p');
  p.setAttribute('data-role', 'invite-message');
  p.textContent = text;
  wrap.appendChild(p);
  if (onRetry) {
    const btn = document.createElement('button');
    btn.setAttribute('data-action', 'retry');
    btn.textContent = t('errorScreen.retryBtn');
    btn.addEventListener('click', () => {
      if (btn.disabled) return;
      btn.disabled = true;
      onRetry();
    });
    wrap.appendChild(btn);
  }
  host.appendChild(wrap);
}

export async function mountInviteView(host: HTMLElement, deps: InviteViewDeps): Promise<void> {
  const preview = await deps.preview(deps.token);
  // Transient (5xx / offline) is recoverable — offer a retry that re-previews on
  // this same route, rather than the terminal "invalid" copy that made a visitor
  // with a working link give up (#5 review follow-up).
  if (preview.status === 'transient') return message(host, t('invite.transient'), () => { void mountInviteView(host, deps); });
  if (preview.status === 'expired') return message(host, t('invite.expired'));
  if (preview.status === 'already_used') return message(host, t('invite.alreadyUsed'));
  if (preview.status === 'invalid') return message(host, t('invite.invalid'));

  const renderInvite = (): void => {
    host.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'login-gate';
    const h = document.createElement('h2');
    h.textContent = t('invite.title', { team: preview.teamName });
    const btn = document.createElement('button');
    btn.setAttribute('data-action', 'accept');
    btn.textContent = t('invite.accept');
    // Guard against a double-click firing two accept POSTs: disable on the first
    // click, before doAccept's first await. Every doAccept branch replaces this
    // button (host.innerHTML = '' → renderInvite/login-gate/mismatch-screen/message
    // all mount fresh elements), so there is no stuck-disabled state to recover from.
    btn.addEventListener('click', () => {
      if (btn.disabled) return;
      btn.disabled = true;
      void doAccept();
    });
    wrap.append(h, btn);
    host.appendChild(wrap);
  };

  const doAccept = async (): Promise<void> => {
    const who = await deps.me();
    if (!who) {
      deps.mountLogin(host, { teamName: preview.teamName, onAuthed: () => { void doAccept(); } });
      return;
    }
    const res = await deps.accept(deps.token);
    if (res.ok) { deps.navigate('/library'); return; }
    if (res.error === 'email_mismatch') {
      host.innerHTML = '';
      const wrap = document.createElement('div');
      wrap.className = 'login-gate';
      const h = document.createElement('h2');
      h.textContent = t('invite.mismatchTitle');
      const p = document.createElement('p');
      p.setAttribute('data-role', 'invite-message');
      p.textContent = t('invite.mismatchBody', { invited: preview.maskedEmail, current: who.email });
      const sw = document.createElement('button');
      sw.setAttribute('data-action', 'switch-account');
      sw.textContent = t('invite.switchAccount');
      // Sign out, then re-drive accept on this SAME /invite/<token> route — NOT
      // navigate('/login'): an authed visitor there gets redirected straight to
      // roleHome (main.ts), stranding them signed in as the wrong account with the
      // token gone from the URL. After signOut, deps.me() returns null → doAccept
      // mounts the login gate here, and the invited address can sign in and retry.
      // Guard against a double-click firing two signOut POSTs + two doAccept()s
      // (the second wiping the first login gate's DOM) — same disabled-on-first-click
      // pattern as the Accept button above; every branch below replaces this
      // button's host (message()/doAccept's re-renders all do `host.innerHTML = ''`),
      // so there's no stuck-disabled state to recover from. On a FAILED signOut,
      // show the failure message instead of silently re-driving doAccept with the
      // same wrong-account session still active (that would just redisplay this
      // identical mismatch screen with no feedback — looks like a broken button).
      sw.addEventListener('click', () => {
        if (sw.disabled) return;
        sw.disabled = true;
        void (async () => {
          const ok = await deps.signOut();
          if (!ok) { message(host, t('invite.failed')); return; }
          void doAccept();
        })();
      });
      wrap.append(h, p, sw);
      host.appendChild(wrap);
      return;
    }
    if (res.error === 'expired') return message(host, t('invite.expired'));
    if (res.error === 'already_used') return message(host, t('invite.alreadyUsed'));
    if (res.error === 'invalid') return message(host, t('invite.invalid'));
    // 401 mid-accept — the session expired between preview and Accept. Re-mount
    // the login gate so the invited address can re-auth and re-drive accept on
    // this same route, exactly like the signed-out path above, instead of the
    // generic dead end (#5 review follow-up).
    if (res.error === 'unauthorized') {
      deps.mountLogin(host, { teamName: preview.teamName, onAuthed: () => { void doAccept(); } });
      return;
    }
    // 403 deactivated — retrying can never succeed, so a terminal message with
    // no retry button (#5 review follow-up).
    if (res.error === 'user_deactivated') return message(host, t('invite.deactivated'));
    // Anything else is unclassified/transient — offer a retry that re-drives
    // accept, rather than stranding the user on a message with no action.
    message(host, t('invite.failed'), () => { void doAccept(); });
  };

  renderInvite();
}
